import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Link, Tooltip, Grid, CircularProgress } from "@material-ui/core";
import PublicIcon from "@material-ui/icons/Public";
import GpsFixedIcon from "@material-ui/icons/GpsFixed";
import LocationSearchingIcon from "@material-ui/icons/LocationSearching";
import HeightIcon from "@material-ui/icons/Height";
import MapIcon from "@material-ui/icons/Map";
import { makeStyles } from "@material-ui/core/styles";

import { roofIcon } from "../util/svg/roofIcon";
import { altitudeIcon } from "../util/svg/altitudeIcon";
import { DownloadButton } from "../util/pdf/DownloadButton";
import { PolicyDescription } from "./PolicyDescription";

import { pdfTableHazardIds } from "../util/pdf/pdfTableHazardIds";
import getHazardsList from "../riskAssessment/hazardsConfigList/allHazardsList";
import useGetHazardsValues from "../riskAssessment/customHooks/useGetHazardsValues";
import { RiskAssessmentsPdf } from "../riskAssessment/pdf/RiskAssessmentPdf";
import { PolicyDescriptionPdf } from "./pdf/PolicyDescriptionPdf";
import { useClientConfig } from "../../context/clientConfigContext";

const googleMapsBaseUrl = "https://www.google.com/maps/search/?api=1&query=";

const useStyles = makeStyles(() => ({
  copyableLink: {
    userSelect: "text",
  },
  typographyWithHover: {
    marginLeft: "4px",
  },
}));

const DisplayIconField = ({ icon, value, tooltipMsg }) => {
  const classes = useStyles();
  if (!value && value !== 0) return "";

  return (
    <Box display="flex" mr={3}>
      <Tooltip title={tooltipMsg} placement="bottom-start">
        {icon}
      </Tooltip>

      <Typography variant="caption" gutterBottom className={classes.typographyWithHover}>
        {value}
      </Typography>
    </Box>
  );
};

export const PolicyDescriptionWithLocation = (props) => {
  const { t } = useTranslation();
  const config = useClientConfig();

  const { policy, roofarea, buildingHeight, groundLevel } = props;

  const [googleMapsUrl, setGoogleMapsUrl] = useState("");
  const [roofTooltip, setRoofTooltip] = useState("");
  const [adjustedCysLocAddress, setAdjustedCysLocAddress] = useState("");

  const allHazardsConfigs = getHazardsList();

  const pdfHazardsConfigs = allHazardsConfigs.filter((hazardConfig) =>
    pdfTableHazardIds.find((hazardCysId) => hazardConfig.cysId === hazardCysId),
  );

  const { hazardValues: tableRisksData, loading } = useGetHazardsValues({
    hazardsList: pdfHazardsConfigs,
    lat: policy?.location.coordinates[1],
    lng: policy?.location.coordinates[0],
  });

  useEffect(() => {
    if (!policy || !roofarea) return;

    const lonlat = policy?.location?.coordinates
      ? policy.location.coordinates[1] + "," + policy.location.coordinates[0]
      : "0,0";

    const approximateAdress =
      (policy?.addressZIPCode ? policy.addressZIPCode + " " : "") +
      (policy?.addressCity ? policy.addressCity + " " : "") +
      (policy?.addressProvince ? policy.addressProvince + " " : "");

    const adjustedAddress =
      policy?.location?.cys_loc_address && policy.location.cys_loc_address !== "NaN"
        ? policy.location.cys_loc_address
        : approximateAdress === ""
          ? "Reale Mutua Assicurazioni Via Corte d'Appello, 11, 10122 Torino TO, Italy"
          : approximateAdress + ", Italy";

    setAdjustedCysLocAddress(adjustedAddress);

    setGoogleMapsUrl(policy?.location ? googleMapsBaseUrl + adjustedAddress.replace(/ /g, "+") : "");
    setRoofTooltip(
      roofarea !== "N/A" ? t("policy.roofAreaTooltip") + lonlat : t("policy.roofAreaNoneTooltip") + lonlat,
    );
  }, [policy, roofarea, t]);

  return (
    <Box p={1}>
      {policy ? (
        <>
          <PolicyDescription policy={policy}></PolicyDescription>

          <Box display="flex" mt={3} mb={2}>
            <DisplayIconField
              icon={<GpsFixedIcon fontSize="small" />}
              tooltipMsg={t("policy.availability")}
              value={policy.location?.accuracy}
            />

            <DisplayIconField
              icon={<PublicIcon fontSize="small" />}
              tooltipMsg={t("policy.geoLocation") + adjustedCysLocAddress}
              value={
                policy.location?.coordinates[1] && policy.location?.coordinates[0]
                  ? `${policy.location.coordinates[1]}, ${policy.location.coordinates[0]}`
                  : null
              }
            />

            <DisplayIconField
              icon={<LocationSearchingIcon fontSize="small" />}
              tooltipMsg={t("policy.confidence")}
              value={policy.location?.confidence}
            />
          </Box>

          <Box display="flex" mt={3} mb={2}>
            <DisplayIconField icon={roofIcon} tooltipMsg={roofTooltip} value={roofarea + " m²"} />
            <DisplayIconField
              icon={<HeightIcon />}
              tooltipMsg={"Building height at centroid of building polygon"}
              value={buildingHeight + " m"}
            />

            <DisplayIconField
              icon={altitudeIcon}
              tooltipMsg={"Building ground altitude over sea level near centroid of building polygon"}
              value={groundLevel + " m"}
            />
          </Box>

          <Box display="flex">
            <Box display="flex" mr={2} mt={0.5}>
              <Tooltip
                title={t("policy.checkAddressOnGoogleMaps") + adjustedCysLocAddress}
                placement="bottom-start"
              >
                <MapIcon fontSize="small" />
              </Tooltip>

              <Link href={googleMapsUrl} target="_blank" rel="noopener">
                Google map
              </Link>
            </Box>

            {policy && Object.keys(policy) && (
              <DownloadButton
                dataLoading={loading}
                documentComponent={
                  <RiskAssessmentsPdf
                    tableRisksData={tableRisksData}
                    latitude={policy.location.coordinates[1]}
                    longitude={policy.location.coordinates[0]}
                    renderDescription={() => (
                      <PolicyDescriptionPdf
                        policy={policy}
                        roofarea={roofarea}
                        buildingHeight={buildingHeight}
                        groundLevel={groundLevel}
                        clientConfigData={config}
                      />
                    )}
                  />
                }
                fileName={`Valutazione_climatica__${policy.policyNumberSecondary}.pdf`}
              ></DownloadButton>
            )}
          </Box>
        </>
      ) : (
        <Grid container justifyContent="center">
          <CircularProgress />
        </Grid>
      )}
    </Box>
  );
};
